import { gatsbyAppWrapper } from '@templates/app-provider'
import ReactDOM from 'react-dom'

export const wrapRootElement = gatsbyAppWrapper
export const disableCorePrefetching = () => true

const getScrollKey = location => `@@scroll|${location.pathname}${location.search}`

/**
 * Called when the browser renders a page from the server.
 *
 * During SSR, IntersectionObserver is not available, so we need to load it
 * dynamically when the client is loaded. We also add a scroll event listener,
 * which stores the current scroll position in sessionStorage. When navigating
 * between pages, the scroll position is restored from sessionStorage.
 *
 * @returns {Promise<void>}
 */
export const onClientEntry = async () => {
  if (typeof IntersectionObserver === `undefined`) {
    await import(`intersection-observer`)
  }
  let lastScrollY = 0
  window.addEventListener('scroll', () => {
    lastScrollY = window.scrollY
    const key = getScrollKey(window.location)
    sessionStorage.setItem(key, JSON.stringify([0, !sessionStorage.getItem(key) ? 0 : lastScrollY]))
  })
}

/**
 * Updates the global routes array to track navigation history.
 * If the current URL is different from the last entry in the routes array,
 * it appends the current URL to the array. It also updates the global
 * previousPath variable to the second last entry in the routes array.
 *
 * @param {object} location - The current location object.
 * @param {object} prevLocation - The previous location object.
 */

export const onRouteUpdate = ({ location, prevLocation }) => {
  window.routes = window.routes || [document.referrer]
  if (window.routes[window.routes.length - 1] !== window.location.href) {
    window.routes.push(window.location.href)
  }
  window.previousPath = window.routes[window.routes.length - 2]
}

/**
 * Replaces the default Gatsby hydrate function with a simple ReactDOM.render()
 * call. This is necessary because the default hydrate function tries to find
 * an existing DOM node to rehydrate, but in our case we've already rendered
 * the app in the browser and need to re-render a new DOM tree. This function
 * is passed to the Gatsby API through the `replaceHydrateFunction` export.
 *
 * @returns {(element: React.ReactElement, container: HTMLElement, callback?: () => void) => void} A function that takes the element to render, the container to render into, and an optional callback to run after rendering.
 */
export function replaceHydrateFunction() {
  return (element, container, callback) => {
    ReactDOM.render(element, container, callback)
  }
}

/**
 * Determines whether the scroll position should be updated when navigating.
 * Retrieves the saved scroll position from sessionStorage using a key derived
 * from the current location. If a saved position exists, the window is scrolled
 * to that position; otherwise, it scrolls to the top. Returns false to prevent
 * Gatsby's default scroll restoration.
 *
 * @param {object} routerProps - The router properties.
 * @param {object} routerProps.location - The current location object.
 * @returns {boolean} Always returns false to disable default scroll behavior.
 */

export function shouldUpdateScroll({ routerProps: { location } }) {
  const { hash } = location

  // PRIORITY 1: If there's a hash in the URL, don't interfere with hash scrolling
  if (hash) {
    return false
  }

  const scrollKey = getScrollKey(location)
  const savedPosition = sessionStorage.getItem(scrollKey)

  // PRIORITY 2: If we have a scrollPosition in state, use that
  if (location.state?.scrollPosition !== undefined) {
    window.scrollTo(0, location.state.scrollPosition)
    window.history.replaceState({ ...location.state, scrollPosition: undefined }, '')
    return false
  }

  // PRIORITY 3: Otherwise fall back to the saved position
  if (savedPosition) {
    const [x, y] = JSON.parse(savedPosition)
    window.scrollTo(x, y)
  } else {
    window.scrollTo(0, 0) // Scroll to top if no saved position
  }

  return false // Prevent Gatsby's default scroll restoration
}
